import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Sidebar from "../../component/Sidebar/SideBar";
import Container from "react-bootstrap/esm/Container";
import Row from "react-bootstrap/esm/Row";
import Col from "react-bootstrap/esm/Col";
import { useTranslation } from "react-i18next";
import { AuthUserContext } from "../../context/context";
import getPackagesDetails from "../../services/packages/getPackages";
import { USER_DETAILS } from "../../constants/localstorageKeys";
import { toast } from "react-toastify";
import DataTables from "../../component/DataTable";
import { useNavigate } from "react-router-dom";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";
import { images } from "../../utils/image";
import ExportToCSV from "../../component/ExportToCSV";
import MyPackageFilters from "./MyPackageFilters";
import { FaFilter, FaSearch } from "react-icons/fa";

const getDataAmount = (row) =>
  String(row?.data ?? "").replace("GB", "").trim();

const getDataFilterValue = (row) => {
  const dataAmount = getDataAmount(row);
  return parseFloat(dataAmount) > 0 ? dataAmount : "unlimited";
};

const getExpirationDate = (row) =>
  new Date(
    new Date(row?.created_at).getTime() +
      Number(row?.validity || 0) * 24 * 60 * 60 * 1000
  );

const isPackageActive = (row) =>
  new Date() - new Date(row?.created_at) <=
  Number(row?.validity || 0) * 24 * 60 * 60 * 1000;

const getStatusLabel = (row) => (isPackageActive(row) ? "Active" : "Expire");

const formatDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const getOriginalPrice = (row) =>
  row?.event === "topup"
    ? parseFloat(row?.price / 100).toFixed(2)
    : parseFloat(row?.price).toFixed(2);

const getFinalPrice = (row) =>
  row?.discountedPrice
    ? parseFloat(row?.discountedPrice).toFixed(2)
    : parseFloat(row?.price).toFixed(2);

const normalizeText = (value) => String(value ?? "").toLowerCase();

const MyPackage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentLocalization, setToastId, user, setUser } =
    useContext(AuthUserContext);
  const [search, setSearch] = useState("");
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    vendor: "",
    status: "",
    data: "",
    validity: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    vendor: "",
    status: "",
    data: "",
    validity: "",
  });
  const [sortConfig, setSortConfig] = useState({
    key: "",
    direction: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  const getDataLabel = useCallback((row) => {
    const dataAmount = getDataAmount(row);
    return parseFloat(dataAmount) > 0
      ? `${dataAmount} ${t("home.part22")}`
      : t("home.part33");
  }, [t]);

  const getRemainingDataLabel = useCallback((row) => {
    const dataAmount = getDataAmount(row);
    if (row?.dataUsage?.remaining && parseFloat(dataAmount) > 0) {
      return `${(row.dataUsage.remaining / 1024).toFixed(2)} ${t(
        "home.part22"
      )}`;
    }

    return parseFloat(dataAmount) > 0
      ? `${dataAmount} ${t("home.part22")}`
      : t("home.part33");
  }, [t]);

  const packageSearchText = useCallback(
    (row) => [
      row?.vendorType,
      row?.sims?.[0]?.iccid,
      row?.userId?.name,
      row?.paymentId?.name,
      row?.userId?.phone_no,
      row?.userId?.email,
      row?.package,
      formatDate(new Date(row?.created_at)),
      formatDate(getExpirationDate(row)),
      row?.validity,
      getRemainingDataLabel(row),
      getDataLabel(row),
      getOriginalPrice(row),
      row?.discountedPer ? `${row.discountedPer}%` : "-",
      getFinalPrice(row),
      getStatusLabel(row),
    ]
      .map(normalizeText)
      .join(" "),
    [getDataLabel, getRemainingDataLabel]
  );

  const filteredPackages = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    return packages.filter((row) => {
      const dataFilterValue = getDataFilterValue(row);
      const rowStatus = isPackageActive(row) ? "active" : "expired";

      if (searchValue && !packageSearchText(row).includes(searchValue)) {
        return false;
      }

      if (appliedFilters.vendor && row?.vendorType !== appliedFilters.vendor) {
        return false;
      }
      if (appliedFilters.status && rowStatus !== appliedFilters.status) {
        return false;
      }
      if (appliedFilters.data && dataFilterValue !== appliedFilters.data) {
        return false;
      }
      if (
        appliedFilters.validity &&
        String(row?.validity ?? "") !== appliedFilters.validity
      ) {
        return false;
      }

      return true;
    });
  }, [appliedFilters, packageSearchText, packages, search]);

  const nestedSort = useCallback((obj, path) => {
    if (path === "data") return parseFloat(getDataAmount(obj)) || 0;
    if (path === "expiration") return getExpirationDate(obj).getTime() || 0;
    if (path === "remainingData") {
      return obj?.dataUsage?.remaining
        ? Number(obj.dataUsage.remaining)
        : parseFloat(getDataAmount(obj)) || 0;
    }
    if (path === "originalPrice") return parseFloat(getOriginalPrice(obj)) || 0;
    if (path === "price") return parseFloat(getFinalPrice(obj)) || 0;
    if (path === "status") return getStatusLabel(obj);
    if (path === "userName") return obj?.userId?.name ?? obj?.paymentId?.name;

    return path
      .split(".")
      .reduce(
        (value, key) => (value && value[key] !== undefined ? value[key] : null),
        obj
      );
  }, []);

  const tableData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredPackages;

    return [...filteredPackages].sort((a, b) => {
      const aValue = nestedSort(a, sortConfig.key);
      const bValue = nestedSort(b, sortConfig.key);

      if (aValue === null || aValue === undefined) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      if (bValue === null || bValue === undefined) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }

      if (!Number.isNaN(Number(aValue)) && !Number.isNaN(Number(bValue))) {
        return sortConfig.direction === "asc"
          ? Number(aValue) - Number(bValue)
          : Number(bValue) - Number(aValue);
      }

      return sortConfig.direction === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredPackages, nestedSort, sortConfig]);

  const vendorOptions = useMemo(
    () =>
      [...new Set(packages.map((row) => row?.vendorType).filter(Boolean))].sort(),
    [packages]
  );

  const dataOptions = useMemo(
    () =>
      [
        ...new Set(
          packages
            .map((row) => getDataFilterValue(row))
            .filter((data) => data !== "")
        ),
      ].sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0)),
    [packages]
  );

  const validityOptions = useMemo(
    () =>
      [
        ...new Set(
          packages
            .map((row) => row?.validity)
            .filter((validity) => validity !== undefined && validity !== null)
        ),
      ].sort((a, b) => Number(a) - Number(b)),
    [packages]
  );

  const activeFiltersCount = [
    appliedFilters.vendor,
    appliedFilters.status,
    appliedFilters.data,
    appliedFilters.validity,
  ].filter(Boolean).length;

  const columns = [
    {
      name: <span className="dataTable-text">{t("table.vendor")}</span>,
      key: "vendorType",
      cell: (row) => (
        <div className="text-start column-text d-flex align-items-center">
          <div className="" style={{ width: "55px" }}>
            <img
              src={
                row?.vendorType === "esimgo"
                  ? images.esimgo
                  : row?.vendorType === "esimVault"
                  ? images.esimVault
                  : images.airalo
              }
              alt="offer-icon"
              width={
                row?.vendorType === "esimgo"
                  ? 35
                  : row?.vendorType === "esimVault"
                  ? 50
                  : 50
              }
            />
          </div>
          <span className="ms-2">{row?.vendorType}</span>
        </div>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: <span className="dataTable-text">{t("table.simid")}</span>,
      key: "sims.0.iccid",
      selector: (row) => (
        <span className="column-text">{row?.sims[0]?.iccid}</span>
      ),
      sortable: true,
      width: "250px",
    },
    {
      name: <span className="dataTable-text">{t("table.username")}</span>,
      key: "userName",
      cell: (row) => (
        <span className="text-start column-text">
          {row?.userId?.name ?? row?.paymentId?.name}
        </span>
      ),
      sortable: true,
      width: "200px",
    },
     {
      name: <span className="dataTable-text">{t("table.phone_no")}</span>,
      key: "userId.phone_no",
      cell: (row) => (
        <span className="text-start column-text">{row?.userId?.phone_no}</span>
      ),
      sortable: true,
      width: "200px",
    },
    {
      name: <span className="dataTable-text">{t("table.email")}</span>,
      key: "userId.email",
      selector: (row) => <span className="column-text">{row?.userId?.email}</span>,
      sortable: true,
      width: "280px",
    },
    {
      name: <span className="dataTable-text">{t("table.packageName")}</span>,
      key: "package",
      selector: (row) => row?.package,
      cell: (row) => (
        <span className="text-start column-text">
          {row.package && row.package?.length > 35
            ? `${row.package.slice(0, 35)}...`
            : row.package}
        </span>
      ),
      sortable: true,
      width: "200px",
    },
    {
      name: <span className="dataTable-text"> {t("table.expiration")}</span>,
      key: "expiration",
      selector: (row) => (
        <span className="column-text">
          {formatDate(getExpirationDate(row))}
        </span>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: <span className="dataTable-text">{t("table.validDays")}</span>,
      key: "validity",
      selector: (row) => (
        <span className="column-text">
          {row?.validity} {t("home.part21")}
        </span>
      ),
      sortable: true,
      width: "160px",
    },
    {
      name: <span className="dataTable-text">{t("table.remainingData")}</span>,
      key: "remainingData",
      selector: (row) => (
        <span className="text-start column-text">{getRemainingDataLabel(row)}</span>
      ),
      sortable: true,
      width:
        currentLocalization === "in" || currentLocalization === "fr"
          ? "210px"
          : "185px",
    },
    {
      name: <span className="dataTable-text">{t("table.Data")}</span>,
      key: "data",
      selector: (row) => (
        <span className="column-text">{getDataLabel(row)}</span>
      ),
      sortable: true,
      width: "90px",
    },
    {
      name: <span className="dataTable-text">{t("table.orignalPrice")}</span>,
      key: "originalPrice",
      selector: (row) => (
        <span className="text-start column-text">${getOriginalPrice(row)}</span>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: <span className="dataTable-text">{t("table.Discount")}</span>,
      key: "discountedPer",
      selector: (row) => (
        <span className="text-start column-text">
          {row.discountedPer ? row.discountedPer + "%" : "-"}
        </span>
      ),
      sortable: true,
      width: "120px",
    },
    {
      name: <span className="dataTable-text">{t("table.Price")}</span>,
      key: "price",
      selector: (row) => (
        <span className="text-start column-text">${getFinalPrice(row)}</span>
      ),
      sortable: true,
      width: "120px",
    },
    {
      name: <span className="dataTable-text">{t("table.Status")}</span>,
      key: "status",
      selector: (row) => (
        <h5>
          <Badge bg={isPackageActive(row) ? "success" : "danger"}>
            {getStatusLabel(row)}
          </Badge>
        </h5>
      ),
      sortable: true,
      width: "120px",
    },
  ];

  const handleSort = async (column, direction) => {
    setSortConfig({
      key: column?.key || "",
      direction,
    });
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [filterName]: value,
    }));
  };

  const handleClearFilters = () => {
    setSearch("");
    const emptyFilters = {
      vendor: "",
      status: "",
      data: "",
      validity: "",
    };
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSortConfig({
      key: "",
      direction: "",
    });
  };

  const handleApplyFilter = () => {
    setAppliedFilters(filters);
  };

  const renderLoadingState = () => (
    <Row>
      <Col>
        <div className="my-package-page-loader">
          <Spinner animation="border" />
        </div>
      </Col>
    </Row>
  );

  const getPackages = useCallback(async () => {
    setLoading(true);
    try {
      const usersResponse = await getPackagesDetails();
      if (usersResponse.success) {
        setPackages(usersResponse?.data.userWithPackageId);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        const id = toast.error(t("toastsMessage.part4"), {
          autoClose: false,
          closeOnClick: false,
          draggable: false,
          pauseOnHover: true,
        });
        setToastId(id);
        window.scroll(0, 0);
        localStorage.clear();
        navigate("/log-in");
      } else {
        console.log("err", err);
        const id = toast.error(err.message, {
          autoClose: false,
          closeOnClick: false,
          draggable: false,
          pauseOnHover: true,
        });
        setToastId(id);
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, setToastId, t]);

  useEffect(() => {
    const userDetails = JSON.parse(localStorage.getItem(USER_DETAILS));
    if (userDetails) {
      setUser(userDetails);
    }
    getPackages();
  }, [currentLocalization, getPackages, setUser]);

  return (
    <Sidebar>
      <Container>
        <Row className="d-flex align-items-center justify-content-between my-package-header">
          <Col
            lg={4}
            md={12}
            className={`${
              currentLocalization === "ar" ? "text-end" : "text-start"
            }`}
          >
            <h2 className="package-h2 my-package-title">
              {t(
                user && user.role === "user"
                  ? "dataUsage.myPackage"
                  : "dataUsage.sim"
              )}
            </h2>
          </Col>
          {!loading && (
            <Col
              lg={8}
              md={12}
              className="d-flex justify-content-end align-items-center flex-wrap gap-2 my-package-toolbar"
            >
              <div className="my-package-search d-flex align-items-center">
                <div className="my-package-search-field">
                  <input
                    type="text"
                    name="search"
                    placeholder="Search"
                    className="my-package-search-input"
                    onChange={(event) => setSearch(event.target.value)}
                    value={search}
                  />
                  <FaSearch className="my-package-search-icon" size={18} />
                </div>
              </div>
              <button
                type="button"
                className={`my-package-filter-toggle ${
                  showFilters ? "active" : ""
                }`}
                onClick={() =>
                  setShowFilters((prevShowFilters) => !prevShowFilters)
                }
                aria-label="Filters"
                title="Filters"
              >
                <FaFilter size={16} />
                {activeFiltersCount > 0 && (
                  <span className="my-package-filter-count">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
              <ExportToCSV
                loading={loading}
                sortedUsers={tableData}
                packages={[]}
                buttonStyle={{ marginTop: 0, height: "46px" }}
              />
            </Col>
          )}
        </Row>

        {loading ? (
          renderLoadingState()
        ) : (
          <>
            {showFilters && (
              <Row>
                <Col xs={12}>
                  <MyPackageFilters
                    filters={filters}
                    vendorOptions={vendorOptions}
                    dataOptions={dataOptions}
                    validityOptions={validityOptions}
                    onFilterChange={handleFilterChange}
                    onApplyFilter={handleApplyFilter}
                    onClearFilters={handleClearFilters}
                  />
                </Col>
              </Row>
            )}
            <Row>
              <Col>
                <div className="mt-4">
                  <DataTables
                    columns={columns}
                    data={tableData}
                    loading={loading}
                    handleSort={handleSort}
                  />
                </div>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </Sidebar>
  );
};

export default MyPackage;
